import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { usePerformanceBenchmarkReport } from "@/hooks/reports/performance-benchmark-report.hook";
import { cn } from "@/lib/utils";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { FC, HTMLAttributes } from "react";
import { Bar } from "react-chartjs-2";
import { useParams } from "react-router-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

type SectorPositioningCardProps = HTMLAttributes<HTMLDivElement>;

const SectorPositioningCard: FC<SectorPositioningCardProps> = ({
  className,
  ...props
}) => {
  const { data: userProfile } = useCurrentUserProfile();

  const { quizId } = useParams();

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: quizId || "",
  });

  const { data: report, isLoading: isReportLoading } =
    usePerformanceBenchmarkReport(
      {
        companyId: userProfile?.companyId || "",
        editionId: quiz?.edition.id || "",
        group: "SECTOR",
      },
      !!quiz?.edition.id,
    );

  const data = {
    labels: ["Promedio", "Tu empresa"],
    datasets: [
      {
        label: "Puntaje",
        data: [report?.groupAverageResult.score, report?.result.score],
        backgroundColor: ["#615CB7", report?.result.color || "#4ABFED"],
        borderRadius: 15,
        barThickness: 40,
      },
    ],
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        ticks: {
          stepSize: 20,
        },
        min: 0,
        max: 100,
      },
    },
  };

  return (
    <Card className={cn(className, "rounded-xl px-5 py-3")} {...props}>
      <div className="flex items-center">
        <p className="mb-1 mt-2 flex-1 text-xl font-bold text-[#13171B]">
          Posición por sector
        </p>

        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-[#4ABFED]" />
          <p className="text-sm font-semibold text-[#A0ACBA]">Tu empresa</p>
        </div>
      </div>

      <p className="mb-6 text-sm font-light text-[#8090A3]">
        Posición relativa de tu empresa respecto al resto de empresas
        correspondientes a tu sector: {quiz?.applicant.sector.name}.
      </p>

      {report && <Bar data={data} options={options} />}

      {isReportLoading && (
        <Skeleton style={{ width: "100%", height: "200px" }} />
      )}
    </Card>
  );
};

export default SectorPositioningCard;
